import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "../components/index.js";
import { useApi, useApiAction } from "../hooks/useApi.js";
import { useAuth } from "../context/AuthContext.jsx";
import { ProductOrderList } from "../components/productOrders/ProductOrderList.jsx";
import { ProductOrderDetail } from "../components/productOrders/ProductOrderDetail.jsx";
import { ProductOrderForm } from "../components/productOrders/ProductOrderForm.jsx";
import {
  PRODUCT_TYPE_LABELS, acquireSessionStorage, buildCreatePayload, classifyFailure, clearPendingCreate, formatDateOnly, readPendingCreate, requireCreateReply, savePendingCreate,
} from "../lib/productOrders.js";

/**
 * Orders — /agency/orders. Admins record a client's print or promotional
 * request, see it through quote and approval, and hand it to billing.
 *
 * THE CREATE IS THE DELICATE PART. The request is remembered — exact payload
 * and submission token — in sessionStorage under the signed-in user's id
 * BEFORE it is sent. From then on:
 *
 *   verified success          the reply is validated; the pending record is
 *                             cleared; the new order opens
 *   400 / 403 / 404           nothing was written: the record is cleared and
 *                             the errors shown; the next attempt mints a new
 *                             token
 *   409                       the token is already used for something else:
 *                             the record is KEPT as evidence, nothing new is
 *                             created, and the list is there to review.
 *                             Discarding is a deliberate click
 *   no reply / 5xx / unreadable  the outcome is UNKNOWN: the server may have
 *                             committed. The form is frozen on exactly what
 *                             was sent and the only way forward is "Retry
 *                             same request" — the SAME token, which the API
 *                             answers with the order the first attempt made
 *                             if it did. No other order can be started
 *   reload                    the record is read back for this user only and
 *                             the same frozen form and retry are shown
 *
 * If sessionStorage is unavailable the request is kept in memory, the user
 * is told a reload will NOT recover it, and retry still works on this page.
 */
export function AgencyOrders() {
  const { user } = useAuth();
  const api = useApiAction();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("order");
  const clientsApi = useApi("/api/me/clients");
  const clients = (clientsApi.data?.clients ?? []).filter((c) => typeof c?.id === "string" && typeof c?.name === "string").slice().sort((a, b) => a.name.localeCompare(b.name));
  const [refreshKey, setRefreshKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [createError, setCreateError] = useState(null);
  // { payload, phase: "sending" | "unknown" | "conflict", storageOk, restored }
  const [pending, setPending] = useState(null);
  // Acquired through a guard: the sessionStorage GETTER itself can throw when
  // storage is blocked. Null here means "no reload recovery", said out loud
  // below; the same-token retry still works from memory.
  const storage = useMemo(() => acquireSessionStorage(typeof window === "undefined" ? undefined : window), []);
  const actorId = user?.id;

  const select = useCallback((id) => setParams((p) => { if (id) p.set("order", id); else p.delete("order"); return p; }, { replace: true }), [setParams]);
  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);

  // A request this user sent before a reload, whose outcome was never seen.
  useEffect(() => {
    if (!actorId) return;
    const remembered = readPendingCreate(storage, actorId);
    if (remembered) { setPending({ payload: remembered, phase: "unknown", storageOk: true, restored: true }); setCreating(true); }
  }, [actorId, storage]);

  async function send(payload, meta) {
    setPending({ payload, phase: "sending", ...meta });
    setCreateError(null);
    try {
      const body = await api("/api/product-orders", { method: "POST", body: JSON.stringify(payload) });
      const { order, replayed } = requireCreateReply(body);
      clearPendingCreate(storage, actorId);
      setPending(null);
      setCreating(false);
      setFieldErrors({});
      setCreateError(replayed ? { tone: "info", text: "That request had already been recorded — this is the order it made." } : null);
      select(order.id);
      bump();
    } catch (e) {
      const kind = classifyFailure(e);
      if (kind === "unauthorized") return; // logged out; the record stays for the next sign-in
      if (kind === "rejected") {
        // Nothing was written. The next attempt is a new request under a new token.
        clearPendingCreate(storage, actorId);
        setPending(null);
        setFieldErrors(e.details && typeof e.details === "object" ? e.details : {});
        setCreateError({ tone: "error", text: e.message || "The request was refused." });
      } else if (kind === "conflict") {
        setPending({ payload, phase: "conflict", ...meta });
        setCreateError({ tone: "warning", text: "This request's identity is already used by a different request. Nothing new was created. Review the orders below before recording it again." });
      } else {
        setPending({ payload, phase: "unknown", ...meta });
        setCreateError({ tone: "warning", text: `We couldn't confirm whether the order was recorded${e?.message ? ` (${e.message})` : ""}. Nothing has been changed on this screen. Use “Retry same request” — it resends exactly this request, and if the first one was recorded you get that order back, not a second one.` });
      }
    }
  }

  function submit(form) {
    if (pending || !actorId) return;
    const submissionToken = crypto.randomUUID();
    const built = buildCreatePayload(form, submissionToken);
    setFieldErrors(built.errors);
    if (!built.payload) return;
    const storageOk = savePendingCreate(storage, actorId, built.payload);
    send(built.payload, { storageOk, restored: false });
  }

  const frozen = pending !== null;

  return (
    <div className="max-w-[1200px] space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(360px,440px)] gap-4 items-start">
        <ProductOrderList mode="admin" clients={clients} selectedId={selectedId} refreshKey={refreshKey}
          onSelect={(id) => { if (!frozen) { setCreating(false); select(id); } }}
          onCreate={() => { if (!frozen) { select(null); setCreating(true); setFieldErrors({}); setCreateError(null); } }} />

        {creating ? (
          <Card>
            <div className="px-5 py-4 border-b border-border-light flex items-start justify-between gap-3">
              <div>
                <div className="text-[13px] font-semibold text-text-primary leading-tight">New order</div>
                <div className="text-[11px] text-text-muted mt-0.5">Recorded on the client's behalf. It starts as <strong>Awaiting quote</strong> — no price is entered here.</div>
              </div>
              {!frozen && <button type="button" onClick={() => { setCreating(false); setFieldErrors({}); setCreateError(null); }} className="text-[12px] text-text-muted hover:text-text-primary" aria-label="Close new order">✕</button>}
            </div>
            {storage === null && (
              <div role="status" className="px-5 py-2.5 text-[12px] border-b border-border-light text-[#8a4b1a] bg-[#fbeae0]">
                This browser is not letting Prism keep a request across a reload. If the outcome of recording an order is ever unclear, keep this page open and use Retry — a reload here will not recover it.
              </div>
            )}

            {pending && (
              <div role="alert" className="px-5 py-3 text-[12px] border-b border-border-light bg-[#fbeae0] text-[#8a4b1a] space-y-2">
                <div className="font-medium">
                  {pending.phase === "sending" ? "Sending…" : pending.phase === "conflict" ? "Not recorded — identity already used" : pending.restored ? "An unfinished order from before the reload" : "Outcome unknown"}
                </div>
                {createError && pending.phase !== "sending" && <div>{createError.text}</div>}
                {!pending.storageOk && <div>This browser can't keep the request across a reload — keep this page open and use Retry.</div>}
                <div className="text-text-secondary">
                  Sent: {PRODUCT_TYPE_LABELS[pending.payload.productType]} · {clients.find((c) => c.id === pending.payload.clientId)?.name ?? pending.payload.clientId} · qty {pending.payload.quantity} · delivery {formatDateOnly(pending.payload.requestedDeliveryDate)}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {pending.phase === "unknown" && (
                    <button type="button" onClick={() => send(pending.payload, { storageOk: pending.storageOk, restored: pending.restored })} className="px-3 py-1.5 rounded-card bg-accent text-white text-[12px] font-medium">Retry same request</button>
                  )}
                  {pending.phase === "conflict" && (
                    <>
                      <button type="button" onClick={() => bump()} className="px-3 py-1.5 rounded-card bg-accent text-white text-[12px] font-medium">Refresh the list</button>
                      <button type="button" onClick={() => { clearPendingCreate(storage, actorId); setPending(null); setCreateError(null); }} className="px-3 py-1.5 rounded-card border border-border-default text-[12px] text-text-secondary">Discard this request</button>
                    </>
                  )}
                </div>
              </div>
            )}
            {createError && !pending && (
              <div role={createError.tone === "info" ? "status" : "alert"} className={`px-5 py-2.5 text-[12px] border-b border-border-light ${createError.tone === "error" ? "text-negative" : "text-positive"}`}>{createError.text}</div>
            )}

            <div className="px-5 py-4">
              <ProductOrderForm
                key={pending ? `pending-${pending.payload.submissionToken}` : "fresh"}
                mode="create"
                clients={clients}
                initial={pending ? pending.payload : undefined}
                errors={fieldErrors}
                pending={frozen}
                disabled={clientsApi.loading || !actorId}
                onSubmit={submit}
              />
            </div>
          </Card>
        ) : selectedId ? (
          <ProductOrderDetail id={selectedId} mode="admin" onClose={() => select(null)} onChanged={bump} />
        ) : (
          <Card><div className="px-5 py-10 text-center text-[13px] text-text-muted">Select an order, or record a new one.</div></Card>
        )}
      </div>
    </div>
  );
}
